import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { fmtMoney, fmtDateTime, ACCOUNT_STATUS } from "@/lib/format";
import { PageHeader, StatusBadge, Empty } from "@/components/kit";

const CreateClient = ({ open, onClose, onDone }) => {
  const empty = { name: "", email: "", phone: "", password: "", status: "active", initial_demo_credit: 10000, account_currency: "USD" };
  const [f, setF] = useState(empty);
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/admin/clients", { ...f, initial_demo_credit: Number(f.initial_demo_credit) }); toast.success(`Cliente ${data.client_code} criado`); setF(empty); onDone(); onClose(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="create-client-dialog">
        <DialogHeader><DialogTitle>Cadastrar novo cliente</DialogTitle>
          <DialogDescription>O cliente receberá (simulado) um e-mail de verificação. Informe a senha inicial com segurança.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {[["name", "Nome completo", "text"], ["email", "E-mail", "email"], ["phone", "Telefone", "tel"], ["password", "Senha inicial (mín. 8, letras e números)", "text"]].map(([k, l, t]) => (
            <div key={k} className="space-y-1.5"><Label>{l}</Label><Input type={t} required className="h-11" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`new-client-${k}-input`} /></div>))}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Status inicial</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger className="h-11" data-testid="new-client-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["active", "pending", "under_review"].map((s) => <SelectItem key={s} value={s}>{ACCOUNT_STATUS[s].label}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Saldo virtual inicial ({f.account_currency === "EUR" ? "€" : "US$"})</Label><Input type="number" min="0" className="h-11 num" value={f.initial_demo_credit} onChange={(e) => setF({ ...f, initial_demo_credit: e.target.value })} data-testid="new-client-credit-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Moeda da conta</Label>
            <Select value={f.account_currency} onValueChange={(v) => setF({ ...f, account_currency: v })}><SelectTrigger className="h-11" data-testid="new-client-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="USD">Dólar (US$)</SelectItem><SelectItem value="EUR">Euro (€)</SelectItem></SelectContent></Select></div>
          <DialogFooter><Button type="submit" className="h-11" data-testid="new-client-submit">Cadastrar cliente</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminClients() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const status = params.get("status") || "all";
  const load = () => api.get("/admin/clients", { params: { q: q || undefined, status: status === "all" ? undefined : status } }).then((r) => setRows(r.data));
  usePoll(() => load().catch(() => {}), 20000, [q, status]);
  return (
    <div data-testid="admin-clients-page">
      <PageHeader eyebrow="Gestão" title="Clientes" desc="Cadastre, pesquise e gerencie as contas dos clientes."
        actions={<Button className="h-11 rounded-xl" onClick={() => setOpen(true)} data-testid="open-create-client-btn"><Plus className="mr-2 h-4 w-4" />Novo cliente</Button>} />
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1"><Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Pesquisar por nome, e-mail ou identificador" className="h-11 pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="client-search-input" /></div>
        <Select value={status} onValueChange={(v) => setParams(v === "all" ? {} : { status: v })}>
          <SelectTrigger className="h-11 sm:w-52" data-testid="client-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos os status</SelectItem>{Object.entries(ACCOUNT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead><tr className="text-left eyebrow border-b border-border"><th className="py-3 pl-5">Identificador</th><th>Cliente</th><th>Status</th><th>E-mail</th><th className="text-right">Saldo (virtual)</th><th>Criado</th><th /></tr></thead>
          <tbody>{rows.map((c) => (
            <tr key={c.id} className="border-b border-border hover:bg-accent/40" data-testid={`client-row-${c.id}`}>
              <td className="py-3 pl-5 num text-primary">{c.client_code}</td>
              <td><div className="font-semibold">{c.name}</div><div className="text-xs text-muted-foreground">{c.email} · {c.phone}</div></td>
              <td><StatusBadge map={ACCOUNT_STATUS} value={c.status} /></td>
              <td className="text-xs">{c.email_verified ? <span className="up">Verificado</span> : <span className="text-amber-500">Não verificado</span>}</td>
              <td className="text-right num">{fmtMoney(c.cash, c.account_currency || "USD")}</td>
              <td className="text-xs text-muted-foreground">{fmtDateTime(c.created_at)}</td>
              <td className="pr-4 text-right"><Button asChild size="sm" variant="outline"><Link to={`/admin/clientes/${c.id}`} data-testid={`client-open-${c.id}`}>Abrir</Link></Button></td>
            </tr>))}</tbody>
        </table>
        {rows.length === 0 && <Empty text="Nenhum cliente encontrado." />}
      </div>
      <CreateClient open={open} onClose={() => setOpen(false)} onDone={load} />
    </div>
  );
}
